// Copyright (c) Docugami, Inc. All rights reserved.

import { createFolderId } from "components/file_management/file_helpers";
import { getChunkLabel } from "hooks/LabelChunk";
import { DgmlInstancesSingleton } from "hooks/documentDgml";
import { BindEntryAction, BindingProviderId, LabelledSpan, ReportEntryType, ReportSettings, ReportViewContext } from "networking/reportsApi";
import { AppDispatch, AppState } from "store";
import { dgmlSlice } from "store/dgml/dgmlSlice";
import { GetBindingsInput, GetEvaluationsState, OperationMetadata, createOperationId, getBindingEvaluationKey, reportDocumentsAdapter, reportsApiSlice, selectBindingsById, selectEntryById, selectPatchedReportBindingsState, selectPatchedReportSchemaState, selectGetReviewDocumentsState } from "store/reports/reportsSlice";
import { createSelectEntryTitles, selectReportContext, selectReportEntryBindings, selectReportSchema } from "store/reports/selectors";
import { isValidDecodedTag } from "utils/TagStandardUtils";

export type AddEntryInput = {
    folderId: string;
    instanceId: string;
    label?: string;
    index?: number;
    groupId?: string;
}

export interface SetBindingsInput {
    folderId: string;
    entryId: string;
    spanIds?: string[];
}

export interface AddBindingInput {
    folderId: string;
    entryId: string;
    instanceId?: string;
    prevInstanceId?: string;
}

export interface RemoveBindingInput {
    folderId: string;
    entryId: string;
    instanceId: string;
}

export type AddEntryWithInstancesInput = {
    folderId: string;
    spanIds: string[];
    label: string;
    index?: number;
    groupId?: string;
}

export const setBindingsThunk = (reportId: string, input: SetBindingsInput, replace: boolean, fixedCacheKey?: string) => async (dispatch: AppDispatch, getState: () => AppState) => {
    const schemaState = selectPatchedReportSchemaState(getState(), reportId);
    const entry = schemaState?.entries.entities[input.entryId];
    if (!entry) {
        // Schema is not loaded or target entry doesn't exist
        return;
    }

    const context = getState().reportsSlice.context;
    if (!context) {
        return;
    }

    // The dgml version is required if we are adding non-empty bindings
    const dgmlVersion = dispatch(tryGetDgmlVersion(reportId, input.folderId));
    if (!dgmlVersion && !!input.spanIds?.length) {
        return;
    }

    await dispatch(reportsApiSlice.endpoints.setEntryBindings.initiate({
        reportId: reportId,
        context: context,
        request: {
            folderId: input.folderId,
            entryId: input.entryId,
            spanIds: input.spanIds,
            replace: replace,
            dgmlVersion: dgmlVersion,
            operationId: createOperationId(),
        }
    }, {
        fixedCacheKey: fixedCacheKey
    }));
}

export const addBindingThunk = (reportId: string, input: AddBindingInput, fixedCacheKey?: string) => async (dispatch: AppDispatch, getState: () => AppState) => {
    const schemaState = selectPatchedReportSchemaState(getState(), reportId);
    const entries = schemaState?.entries;

    if (!entries) {
        // We don't have the schema loaded yet
        return;
    }

    const entry = entries.entities[input.entryId];
    if (!entry) {
        // Target entry doesn't exist
        return;
    }

    const context = getState().reportsSlice.context;
    if (!context) {
        return;
    }

    const folderId = createFolderId(input.folderId);
    const getBindingsInput: GetBindingsInput = {
        reportId: reportId,
        fileId: folderId.fileId
    }

    const bindings = selectPatchedReportBindingsState(getState(), getBindingsInput)?.entryBindings.entities[input.entryId]?.bindings;
    if (!!bindings?.find(x => x.spanId === input.instanceId && x.bindingProviderId === BindingProviderId.User)) {
        // This instance is already bound to this entry
        return;
    }

    await dispatch(reportsApiSlice.endpoints.bindEntry.initiate({
        reportId: reportId,
        context: context,
        request: {
            folderId: folderId.value,
            entryId: input.entryId,
            spanId: input.instanceId,
            action: BindEntryAction.Add,
            previousSpanId: input.prevInstanceId,
            operationId: createOperationId()
        }
    }, {
        fixedCacheKey: fixedCacheKey
    }));
}

export const removeBindingThunk = (reportId: string, input: RemoveBindingInput, fixedCacheKey?: string) => async (dispatch: AppDispatch, getState: () => AppState) => {
    const schemaState = selectPatchedReportSchemaState(getState(), reportId);
    const entries = schemaState?.entries;

    if (!entries) {
        // We don't have the schema loaded yet
        return;
    }

    const entry = entries.entities[input.entryId];
    if (!entry) {
        // Target entry doesn't exist
        return;
    }

    if (!input.instanceId) {
        // Nothing to remove
        return;
    }

    const context = getState().reportsSlice.context;
    if (!context) {
        return;
    }

    const folderId = createFolderId(input.folderId);
    const getBindingsInput: GetBindingsInput = {
        reportId: reportId,
        fileId: folderId.fileId
    }

    const allEntryBindings = selectPatchedReportBindingsState(getState(), getBindingsInput)?.entryBindings;
    const entryBindings = allEntryBindings?.entities[input.entryId];
    if (!!entryBindings && DgmlInstancesSingleton.isInitialized(folderId.value)) {
        const existingBinding = entryBindings.bindings.find(x => x.spanId === input.instanceId);
        if (!!existingBinding?.spanId) {
            input.instanceId = existingBinding.spanId;
        }
    }

    await dispatch(reportsApiSlice.endpoints.bindEntry.initiate({
        reportId: reportId,
        context: context,
        request: {
            folderId: folderId.value,
            entryId: input.entryId,
            spanId: input.instanceId,
            action: BindEntryAction.Remove,
            operationId: createOperationId()
        }
    }, {
        fixedCacheKey: fixedCacheKey
    }));
}

export const addEntryThunk = (reportId: string, input: AddEntryInput, fixedCacheKey?: string) => async (dispatch: AppDispatch, getState: () => AppState) => {
    const folderId = input.folderId;
    if (!DgmlInstancesSingleton.isInitialized(folderId)) {
        throw new Error("Cannot add entry without DGML");
    }

    const instance = DgmlInstancesSingleton.getInstanceById(folderId, input.instanceId);
    if (!instance) {
        return;
    }

    const label = input.label || dispatch(getChunkLabel(instance));
    if (!label || !isValidDecodedTag(label)) {
        return;
    }

    const context = getState().reportsSlice.context;
    if (!context) {
        return;
    }

    const existingEntryLabels = createSelectEntryTitles()(getState(), reportId);
    if (existingEntryLabels.includes(label)) {
        // There is an existing entry with this label
        throw new Error("Cannot add entry with duplicate label.");
    }

    await dispatch(reportsApiSlice.endpoints.bindEntry.initiate({
        reportId: reportId,
        context: context,
        request: {
            createEntryDefinition: {
                title: label,
                entryType: ReportEntryType.Text,
                index: input.index,
                groupId: input.groupId
            },
            action: BindEntryAction.Add,
            folderId: folderId,
            spanId: instance.spanId!,
            operationId: createOperationId(),
        }
    }, {
        fixedCacheKey: fixedCacheKey
    }));
}

export const publishReportThunk = (reportId: string, settings: ReportSettings, fixedCacheKey?: string, operationId?: string) => async (dispatch: AppDispatch, getState: () => AppState) => {
    const state = getState();

    const documentState = selectGetReviewDocumentsState(state, reportId)?.documents;
    const folderIds = !!documentState ?
        reportDocumentsAdapter.getSelectors()
            .selectAll(documentState)
            .filter(x => !!state.documents.indexed[x.folderId]?.clusterDocument.hasCpml)
            .map(x => x.folderId) :
        [];

    await dispatch(reportsApiSlice.endpoints.publishReport.initiate({
        reportId: reportId,
        reportSettings: settings,
        folderIds: folderIds,
        operationId: operationId ?? createOperationId()
    }, {
        fixedCacheKey
    }));
}

const tryGetDgmlVersion = (reportId: string, folderId: string, entryId?: string, instanceId?: string) => (dispatch: AppDispatch, getState: () => AppState) => {
    const fid = createFolderId(folderId);
    let dgmlVersion: string | undefined = dgmlSlice.endpoints.getDgmlNodes.select({ docsetId: fid.clusterId, fileId: fid.fileId })(getState()).data?.version?.value;
    if (!!instanceId && !!entryId && !dgmlVersion) {
        const key = getBindingEvaluationKey(instanceId);
        const selectDgmlVersionForInstance = (state?: GetEvaluationsState) => state?.evaluations.entities[key]?.dgmlVersion;
        dgmlVersion = selectDgmlVersionForInstance(reportsApiSlice.endpoints.getEvaluationsPreview.select({reportId, fileId: fid.fileId})(getState())?.data);
    }

    return dgmlVersion;
}

export const addEntryWithInstancesThunk = (reportId: string, input: AddEntryWithInstancesInput, fixedCacheKey?: string) => async (dispatch: AppDispatch, getState: () => AppState) => {
    const folderId = input.folderId;
    if (!DgmlInstancesSingleton.isInitialized(folderId)) {
        // Cannot add new entry without DGML
        return;
    }

    if (!input.spanIds.length) {
        return;
    }

    const label = input.label;
    if (!label || !isValidDecodedTag(label)) {
        return;
    }

    const context = getState().reportsSlice.context;
    if (!context) {
        return;
    }

    const existingEntryLabels = createSelectEntryTitles()(getState(), reportId);
    if (existingEntryLabels.includes(label)) {
        // There is an existing entry with this label
        return;
    }

    const entryResponse = await dispatch(reportsApiSlice.endpoints.addEntry.initiate({
        reportId: reportId,
        definition: {
            title: label,
            entryType: ReportEntryType.Text
        },
        operationId: createOperationId()
    }, {
        fixedCacheKey: fixedCacheKey
    })).unwrap();

    if (!!entryResponse.entryId) {
        await dispatch(reportsApiSlice.endpoints.setEntryBindings.initiate({
            reportId: reportId,
            context: context,
            request: {
                entryId: entryResponse.entryId,
                folderId: input.folderId,
                spanIds: input.spanIds,
                replace: true,
                operationId: createOperationId()
            }
        }, {
            fixedCacheKey: fixedCacheKey
        }));
    }
}

export const bulkConfirmBindings = (folderIds: string[], entryIds: string[], operationMetadata?: OperationMetadata) => async (dispatch: AppDispatch, getState: () => AppState) => {
    const state = getState();
    const context = selectReportContext(state);
    if (context !== ReportViewContext.Review) {
        return;
    }

    if (!folderIds.length) {
        return;
    }

    const labelledSpans: LabelledSpan[] = [];
    const schema = selectReportSchema(state);
    if (!schema) {
        return;
    }

    if (entryIds.length === 0) {
        entryIds = schema.report.entries;
    }

    for (const folderId of folderIds) {
        const entryBindingsState = selectReportEntryBindings(state, createFolderId(folderId).fileId);
        if (!entryBindingsState) {
            continue;
        }

        const fid = createFolderId(folderId);
        for (const entryId of entryIds) {
            const entry = selectEntryById(schema.entries, entryId);
            if (!entry) {
                continue;
            }

            const entryBindings = selectBindingsById(entryBindingsState, entryId);

            if (!entryBindings?.bindings?.length) {
                labelledSpans.push({
                    fileId: fid.fileId,
                    labelId: entry.labelId
                })
            }
            else {
                for (const binding of entryBindings.bindings) {
                    if (!binding.isConfirmed) {
                        labelledSpans.push({
                            fileId: fid.fileId,
                            labelId: entry.labelId,
                            spanId: binding.spanId
                        })
                    }
                }
            }
        }
    }

    if (!!labelledSpans.length) {
        await dispatch(reportsApiSlice.endpoints.bulkConfirmBindings.initiate({
            operationMetadata: operationMetadata,
            request: {
                reportId: schema.report.id,
                labelledSpans: labelledSpans,
                operationId: createOperationId()
            }
        }));
    }
}
